import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ComponentStore } from '@ngrx/component-store';
import { TGlobalTemplateApi } from '@pages/settings/template-settings/template-settings.interface';
import { TemplateSettingsService } from '@pages/settings/template-settings/template-settings.service';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';

interface IDeliverableFlowDialogState {
  error: string | null;
  loading: boolean;
  templates: TGlobalTemplateApi[] | undefined;
}

const initialState: IDeliverableFlowDialogState = {
  error: null,
  loading: false,
  templates: undefined,
};

@Injectable()
export class DeliverableFlowDialogStore extends ComponentStore<IDeliverableFlowDialogState> {
  public readonly error$ = this.selectSignal((state: IDeliverableFlowDialogState) => state.error);
  public readonly loading$ = this.selectSignal((state: IDeliverableFlowDialogState) => state.loading);
  public readonly templates$ = this.selectSignal((state: IDeliverableFlowDialogState) => state.templates);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly fetchGlobalTemplates = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() =>
        this.patchState({
          error: null,
          loading: true,
        }),
      ),
      switchMap(() =>
        this.templateSettingsService
          .getGlobalTemplates(
            new HttpParams().set('order_by', 'platform_feature').set('limit', 1000).set('joins', 'all'),
          )
          .pipe(
            tap((templates: IApiResponse<TGlobalTemplateApi[]>) => {
              this.patchState({
                loading: false,
                templates: templates.data ?? [],
              });
            }),
            this.handleError('Failed to load global templates'),
          ),
      ),
    );
  });

  constructor(private readonly templateSettingsService: TemplateSettingsService) {
    super(initialState);
  }
}
